import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/apiClient";
import { FormulaServiceForm } from "@/components/FormulaServiceForm";
import { Brand } from "@/components/Brand";
import { PrivacyConsent, LegalFooterLinks } from "@/components/LegalConsent";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, FileSignature, CheckCircle2, ShieldCheck } from "lucide-react";

export default function FormulaServicePublic() {
  const { shortId } = useParams();
  const [cfg, setCfg] = useState(null);
  const [value, setValue] = useState(null);
  const [calc, setCalc] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [dlg, setDlg] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ nome: "", cognome: "", codice_fiscale: "", email: "", telefono: "" });
  const [consenso, setConsenso] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: q }] = await Promise.all([
          api.get("/formula-service/config"),
          api.get(`/public/formula-service/${shortId}`),
        ]);
        setCfg(c);
        setValue({ ...q.config, flessibilita_pct: 0 });
        setCalc(q.calc);
      } catch (e) { setNotFound(true); }
    })();
  }, [shortId]);

  useEffect(() => {
    if (!value) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.patch(`/public/formula-service/${shortId}/recalc`, value);
        setCalc(data.calc);
      } catch (e) { /* ignore */ }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [value]); // eslint-disable-line

  const submit = async () => {
    if (!consenso) { toast.error("Accetta l'informativa privacy per procedere."); return; }
    setSending(true);
    try {
      await api.post(`/public/formula-service/${shortId}/richiesta`, { ...form, consenso_privacy: true });
      setDone(true);
      setDlg(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  if (notFound) return (
    <div className="flex min-h-screen items-center justify-center bg-bone p-6">
      <Card className="max-w-md p-8 text-center"><p className="text-forest">Preventivo non trovato o link non più valido.</p></Card>
    </div>
  );
  if (!cfg || !value) return <div className="flex min-h-screen items-center justify-center bg-bone"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;

  return (
    <div className="min-h-screen bg-bone" data-testid="fs-public-page">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-bone/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 md:px-6"><Brand /></div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8 md:px-6">
        <div className="mb-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sage px-3 py-1 text-xs font-medium text-forest"><ShieldCheck className="h-3.5 w-3.5" /> {cfg.product_name}</span>
          <h1 className="mt-3 font-heading text-3xl font-semibold text-forest">La tua proposta Protezione Famiglia</h1>
          <p className="mt-1 text-muted-foreground">Personalizza le garanzie e richiedi il preventivo formale al tuo intermediario.</p>
        </div>

        <FormulaServiceForm cfg={cfg} value={value} onChange={setValue} calc={calc} showFlex={false}>
          {done ? (
            <Card className="rounded-xl border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="fs-request-done">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-600" />
              <p className="text-sm font-medium text-emerald-800">Richiesta inviata!</p>
              <p className="mt-1 text-xs text-emerald-700">L'intermediario ti contatterà a breve con il preventivo formale.</p>
            </Card>
          ) : (
            <Dialog open={dlg} onOpenChange={setDlg}>
              <DialogTrigger asChild>
                <Button data-testid="fs-richiedi-preventivo" className="w-full gap-2 rounded-full bg-terracotta hover:bg-terracotta/90">
                  <FileSignature className="h-4 w-4" /> Richiedi preventivo formale
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="fs-lead-dialog">
                <DialogHeader><DialogTitle>Richiedi il preventivo formale</DialogTitle></DialogHeader>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div><Label className="text-xs">Nome *</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="fs-lead-nome" /></div>
                  <div><Label className="text-xs">Cognome *</Label><Input value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} data-testid="fs-lead-cognome" /></div>
                  <div><Label className="text-xs">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="fs-lead-email" /></div>
                  <div><Label className="text-xs">Telefono *</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="fs-lead-telefono" /></div>
                  <div className="sm:col-span-2"><Label className="text-xs">Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} data-testid="fs-lead-cf" /></div>
                </div>
                <PrivacyConsent checked={consenso} onCheckedChange={setConsenso} testid="fs-lead-privacy" />
                <DialogFooter>
                  <Button onClick={submit} disabled={sending || !consenso} data-testid="fs-lead-submit" className="gap-2 bg-terracotta hover:bg-terracotta/90">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />} Invia richiesta
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </FormulaServiceForm>

        <footer className="mt-10 border-t border-border/60 pt-6 text-center text-xs text-muted-foreground">
          <LegalFooterLinks className="mb-3" />
          Assietica Soc.Coop. · Simulazione informativa, non vincolante.
        </footer>
      </main>
    </div>
  );
}
